import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllCollections,
  selectAllCollectionsError,
  selectAllCollectionsStatus,
} from '../store/collection.selectors';
import {
  getCollectionDetails,
  fetchAllCollections,
  fetchDatasetsForCollection,
} from '../store/collection.actions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CardSkeleton from '../../../utils/common/components/CardSkeleton';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Cards from './Cards';
import { StyledPaper, PageWrapper, TableWrapper } from '../../../utils/common/styles/commonStyles';
import { fetchSigns } from '../../dashboard/store/dashboard.action';
import ManageCollectionModal from './ManageCollectionModal';
import CollectionUsers from './CollectionUsers';

const CollectionModel = ({ handleOpenModal, searchTerm = '' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const collections = useSelector(selectAllCollections);
  const status = useSelector(selectAllCollectionsStatus);
  const error = useSelector(selectAllCollectionsError);

  // State for manage collection modal
  const [manageCollectionOpen, setManageCollectionOpen] = useState(false);
  const [selectedCollectionForCollection, setSelectedCollectionForCollection] = useState(null);

  // State for user management dialog
  const [isUsersListOpen, setIsUsersListOpen] = useState(false);
  const [selectedCollectionForUsers, setSelectedCollectionForUsers] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  const handleManageCollectionOpen = (collection) => {
    setSelectedCollectionForCollection(collection);
    setManageCollectionOpen(true);
  };

  const handleManageCollectionClose = async (toastMsg) => {
    const refreshCompanyId =
      selectedCollectionForCollection?.companyID ||
      selectedCollectionForCollection?.companyId ||
      selectedCollectionForCollection?.companyid;

    setManageCollectionOpen(false);
    setSelectedCollectionForCollection(null);

    if (refreshCompanyId) {
      await dispatch(fetchAllCollections(refreshCompanyId));
    }
    // show toast after refresh
    if (toastMsg) {
      setToast({ ...toastMsg, open: true });
    }
  };

  const handleManageUsers = (collection) => {
    setSelectedCollectionForUsers(collection);
    setIsUsersListOpen(true);
  };

  const handleUsersClose = () => {
    setIsUsersListOpen(false);
    setSelectedCollectionForUsers(null);
  };

  // Check if subscription has expired
  useEffect(() => {
    const companyData = localStorage.getItem('CompanyData');
    const parsedCompanyData = companyData ? JSON.parse(companyData) : null;

    if (parsedCompanyData && parsedCompanyData.subscriptionEndDate) {
      const endDate = new Date(parsedCompanyData.subscriptionEndDate);
      const now = new Date();
      setSubscriptionExpired(endDate <= now);
    }
  }, []);

  // without explicit sort controls the list is just the raw data
  const sortedCollections = useMemo(() => collections, [collections]);

  const filteredAndSortedCollections = useMemo(() => {
    let filtered = sortedCollections;
    if (searchTerm && searchTerm.trim() !== '') {
      const lowerSearchTerm = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((collection) => {
        const searchableFields = [collection.name];
        return searchableFields.some((field) =>
          String(field).toLowerCase().includes(lowerSearchTerm)
        );
      });
    }
    return filtered;
  }, [sortedCollections, searchTerm]);

  if (status === 'loading') {
    return <CardSkeleton count={6} />;
  }

  if (status === 'fail') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  const handleCardClick = (row) => {
    if (subscriptionExpired) {
      setToast({
        open: true,
        message: 'Your subscription has ended. Please make payment to extend your subscription.',
        severity: 'error',
      });
      return;
    }

    const driveCount = row.driveCount ?? (Array.isArray(row.drives) ? row.drives.length : 0);
    if (driveCount === 0) {
      setToast({
        open: true,
        message: 'Add drives to view the collection.',
        severity: 'warning',
      });
      return;
    }

    try {
      const collectionData = {
        id: row.collectionId || row.id,
        name: row.name || row.collectionName,
      };

      localStorage.setItem('createDriveFromCollection', JSON.stringify(collectionData));
      dispatch(fetchDatasetsForCollection(collectionData.id));
    } catch (e) {
      toast('Error saving createDriveFromCollection to localStorage', e);
    }

    dispatch(fetchSigns());
    navigate('/drives');
  };

  return (
    <PageWrapper>
      <TableWrapper>
        <StyledPaper sx={{ minHeight: '70vh', p: 1 }}>
          <Box sx={{ width: '100%' }}>
            {filteredAndSortedCollections?.length ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 2,
                  padding: 2,
                  borderRadius: 1,
                }}
              >
                {filteredAndSortedCollections.map((row) => (
                  <Cards
                    key={row.id}
                    row={row}
                    onCardClick={handleCardClick}
                    onEditClick={(row) => {
                      dispatch(getCollectionDetails(row.id));
                      handleOpenModal();
                    }}
                    onManageCollection={handleManageCollectionOpen}
                    onManageUsers={handleManageUsers}
                    isSubscriptionExpired={subscriptionExpired}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  No collections available.
                </Typography>
              </Box>
            )}
          </Box>
        </StyledPaper>
      </TableWrapper>

      <ManageCollectionModal
        open={manageCollectionOpen}
        onClose={handleManageCollectionClose}
        onUpdate={(msg) => handleManageCollectionClose(msg)}
        collectionId={
          selectedCollectionForCollection?.collectionId || selectedCollectionForCollection?.id
        }
        collectionName={
          selectedCollectionForCollection?.name || selectedCollectionForCollection?.collectionName
        }
        companyId={selectedCollectionForCollection?.companyID}
      />
      {/* toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <CollectionUsers
        isUsersListOpen={isUsersListOpen}
        onClose={handleUsersClose}
        selectedCollection={selectedCollectionForUsers}
      />
    </PageWrapper>
  );
};

export default CollectionModel;

CollectionModel.propTypes = {
  handleOpenModal: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
};
